import { useState, useRef, useEffect } from "react";
import api, { apiError } from "../lib/api";
import {
    LogIn,
    Sparkles,
    Share2,
    Newspaper,
    CalendarDays,
    GraduationCap,
    Inbox,
    Users,
    Image as ImageIcon,
    Send,
    Bot,
    User as UserIcon,
    ChevronDown,
} from "lucide-react";

const GUIDES = [
    {
        icon: LogIn,
        title: "1. Logging in",
        steps: [
            "Open the private team link the owner shared with you (a hard-to-guess address, not linked anywhere public).",
            'Click "Sign in with Google" and choose your Google (Gmail) account.',
            'The first time, you\'ll see a "Pending approval" screen — the owner approves your email, then you sign in again and land on the Dashboard.',
            "There are no passwords — you always sign in with your Google account.",
        ],
    },
    {
        icon: Sparkles,
        title: "2. Create Post — write once, post everywhere",
        steps: [
            'Click "Create Post" in the sidebar.',
            "Type a Title and pick a Date.",
            'Optional: click "Click to upload" to add an image (image files, under 12 MB). It\'s used on the blog and social posts.',
            'Write a short "Social caption" — the text used on social networks. A link back to the full post is added automatically.',
            'Write the "Full post (blog)" body using the toolbar (bold, italic, underline, heading, lists, link).',
            'Under "Publish to", click the networks you want. "Website Blog" is always available; networks marked "Connect first" must be linked on the Channels page.',
            'Leave "Publish the blog post live" checked to go public now, or uncheck to save it as a draft.',
            'Click "Publish everywhere". A Results panel confirms each destination: Published (with a view link), Saved as draft, or Setup required.',
        ],
    },
    {
        icon: Share2,
        title: "3. Channels — your posting hub",
        steps: [
            'Click "Channels" in the sidebar.',
            "See cards for Website Blog, Facebook, Instagram, Threads, LinkedIn, TikTok, Bluesky, Google Business Profile and Pinterest.",
            'Green ("Always on"/"Connected") means ready to post. Amber ("Setup required") means it needs a one-time connection.',
            'Click "Connect" on a network to begin linking it. The Website Blog works fully today; social networks are rolling out one at a time.',
        ],
    },
    {
        icon: Newspaper,
        title: "4. Blog",
        steps: [
            'Click "Blog" to see every post. Click "New post" to add one.',
            "Fill in Title, Date, Cover image, Excerpt, Body, and the Published checkbox.",
            "Use the pencil icon to edit and the trash icon to delete.",
            'Unpublished posts show a "Draft" tag and stay hidden from the public site.',
        ],
    },
    {
        icon: CalendarDays,
        title: "5. Events",
        steps: [
            'Click "Events" then "New event".',
            "Add Title, Date, Time, Location, Description, an optional flyer image, and a registration Link.",
            "Save. Only upcoming events appear on the public Events page.",
        ],
    },
    {
        icon: GraduationCap,
        title: "6. Trainings",
        steps: [
            'Click "Trainings" to add or edit trainings.',
            "Give each a Tag, Title, Body, a link, and a Date.",
        ],
    },
    {
        icon: Inbox,
        title: "7. Messages",
        steps: [
            'Click "Messages" to read what visitors sent through the Contact form.',
            "Mark a message as read, or delete it.",
        ],
    },
    {
        icon: Users,
        title: "8. Access — invite trusted people",
        steps: [
            'Ask the person to open the private team link and click "Sign in with Google" once — this adds them as "Pending".',
            'Open the "Access" tab to see everyone who has signed in and their status.',
            'Click "Approve" next to their email to grant full access; "Revoke" pauses it; the trash icon removes them.',
            "The owner is always approved and can't be revoked. You can't revoke your own access. No passwords anywhere.",
        ],
    },
    {
        icon: ImageIcon,
        title: "9. Uploading images",
        steps: [
            'Wherever you see "Click to upload", you can add an image.',
            "Files must be images and under 12 MB.",
        ],
    },
];

function Guide({ g, open, onToggle }) {
    const Icon = g.icon;
    return (
        <div className="overflow-hidden rounded-2xl border border-blue-500/15 bg-[#0c172e]">
            <button
                data-testid={`howto-guide-${g.title.split(".")[0]}`}
                onClick={onToggle}
                className="flex w-full items-center gap-3 px-5 py-4 text-left"
            >
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-500/10 ring-1 ring-blue-500/25">
                    <Icon className="h-4 w-4 text-sky-400" />
                </span>
                <span className="flex-1 font-serif text-lg text-slate-300">{g.title}</span>
                <ChevronDown
                    className={`h-5 w-5 text-sky-400 transition-transform ${open ? "rotate-180" : ""}`}
                />
            </button>
            {open && (
                <ol className="list-decimal space-y-2 px-6 pb-5 pl-12 text-sm leading-relaxed text-slate-300/70 marker:text-blue-400/70">
                    {g.steps.map((s) => (
                        <li key={s}>{s}</li>
                    ))}
                </ol>
            )}
        </div>
    );
}

const STARTERS = [
    "How do I log in?",
    "How do I post to my blog and socials at once?",
    "How do I add another admin?",
    "What should I post today?",
];

function Assistant() {
    const [sessionId] = useState(
        () =>
            (window.crypto?.randomUUID && window.crypto.randomUUID()) ||
            `sess-${Date.now()}`
    );
    const [messages, setMessages] = useState([
        {
            id: "welcome",
            role: "assistant",
            content:
                "Hi! I'm Sage, your built-in help guide. Ask me how to do anything on this site — or ask me for post and blog ideas. How can I help?",
        },
    ]);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const scrollRef = useRef(null);

    useEffect(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }, [messages, busy]);

    const send = async (text) => {
        const q = (text ?? input).trim();
        if (!q || busy) return;
        setError("");
        setInput("");
        setMessages((m) => [...m, { id: `u-${Date.now()}`, role: "user", content: q }]);
        setBusy(true);
        try {
            const { data } = await api.post("/assistant/chat", {
                session_id: sessionId,
                message: q,
            });
            setMessages((m) => [...m, { id: `a-${Date.now()}`, role: "assistant", content: data.reply }]);
        } catch (err) {
            setError(apiError(err.response?.data?.detail) || "The assistant is unavailable right now.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            data-testid="howto-assistant"
            className="flex h-[560px] flex-col rounded-3xl border border-blue-500/20 bg-[#0c172e] shadow-[0_30px_80px_rgba(0,0,0,0.4)]"
        >
            <div className="flex items-center gap-3 border-b border-blue-500/15 bg-gradient-to-r from-[#162e5c] to-[#0c1935] px-5 py-4">
                <span className="relative flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-b from-[#1e3a8a] to-[#0a1326] ring-1 ring-blue-400/40">
                    <img
                        src="/images/sprite-neuro.png"
                        alt="Professor Neuro"
                        className="h-8 w-8 rounded-lg object-cover neuro-float"
                    />
                    <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-emerald-400 text-emerald-400 led-pulse ring-2 ring-[#0c172e]" />
                </span>
                <div>
                    <p className="font-serif text-lg text-blue-50">Ask Sage</p>
                    <p className="text-xs text-slate-300/50">Your step-by-step help assistant</p>
                </div>
            </div>

            <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto px-5 py-5">
                {messages.map((m) => (
                    <div
                        key={m.id}
                        className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}
                    >
                        <span
                            className={`mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full ring-1 ${
                                m.role === "user"
                                    ? "bg-blue-500/20 ring-blue-500/40"
                                    : "bg-blue-500/10 ring-blue-500/25"
                            }`}
                        >
                            {m.role === "user" ? (
                                <UserIcon className="h-3.5 w-3.5 text-slate-400" />
                            ) : (
                                <Bot className="h-3.5 w-3.5 text-blue-400" />
                            )}
                        </span>
                        <div
                            className={`max-w-[80%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                                m.role === "user"
                                    ? "bg-blue-500/15 text-blue-50"
                                    : "bg-[#070e1c] text-slate-300/85"
                            }`}
                        >
                            {m.content}
                        </div>
                    </div>
                ))}
                {busy && (
                    <div className="flex gap-3">
                        <span className="mt-0.5 flex h-7 w-7 items-center justify-center rounded-full bg-blue-500/10 ring-1 ring-blue-500/25">
                            <Bot className="h-3.5 w-3.5 text-blue-400" />
                        </span>
                        <div className="rounded-2xl bg-[#070e1c] px-4 py-3 text-sm text-slate-300/50">
                            <span className="inline-flex gap-1">
                                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-blue-400/70 [animation-delay:-0.2s]" />
                                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-blue-400/70 [animation-delay:-0.1s]" />
                                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-blue-400/70" />
                            </span>
                        </div>
                    </div>
                )}
            </div>

            {messages.length <= 1 && (
                <div className="flex flex-wrap gap-2 px-5 pb-3">
                    {STARTERS.map((s) => (
                        <button
                            key={s}
                            data-testid="howto-starter"
                            onClick={() => send(s)}
                            className="rounded-full border border-blue-500/25 px-3 py-1.5 text-xs text-slate-300/70 hover:bg-blue-500/10 hover:text-slate-300"
                        >
                            {s}
                        </button>
                    ))}
                </div>
            )}

            {error && <p className="px-5 pb-1 text-xs text-red-400">{error}</p>}

            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    send();
                }}
                className="flex items-center gap-2 border-t border-blue-500/15 p-3"
            >
                <input
                    data-testid="howto-assistant-input"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Ask how to do something…"
                    className="flex-1 rounded-full border border-blue-500/20 bg-[#070e1c] px-4 py-3 text-sm text-blue-50 outline-none focus:border-blue-400"
                />
                <button
                    data-testid="howto-assistant-send"
                    type="submit"
                    disabled={busy || !input.trim()}
                    className="flex h-11 w-11 items-center justify-center rounded-full bg-gradient-to-b from-blue-400 via-blue-500 to-blue-700 text-[#ffffff] hover:brightness-110 disabled:opacity-50"
                >
                    <Send className="h-4 w-4" />
                </button>
            </form>
        </div>
    );
}

export default function HowTo() {
    const [openIdx, setOpenIdx] = useState(0);

    return (
        <div className="min-h-screen bg-[#070c18] px-6 pb-24 pt-28">
            <div className="mx-auto max-w-6xl">
                <div className="text-center">
                    <p className="flex items-center justify-center gap-2 text-xs uppercase tracking-[0.25em] text-sky-400/70">
                        <Sparkles className="h-3.5 w-3.5" /> How to use this site
                    </p>
                    <h1 className="mt-3 font-serif text-4xl text-blue-50 sm:text-5xl">
                        Everything you need, step by step
                    </h1>
                    <p className="mx-auto mt-4 max-w-2xl text-base text-slate-300/60">
                        A complete, plain-language guide to running the site — from logging in to
                        posting everywhere at once. Stuck? Ask Sage, the assistant on the right,
                        anything at all.
                    </p>
                </div>

                <div className="mt-12 grid gap-8 lg:grid-cols-[1fr_420px]">
                    <div data-testid="howto-guides" className="space-y-3">
                        {GUIDES.map((g, i) => (
                            <Guide
                                key={g.title}
                                g={g}
                                open={openIdx === i}
                                onToggle={() => setOpenIdx(openIdx === i ? -1 : i)}
                            />
                        ))}
                    </div>

                    <div className="lg:sticky lg:top-28 lg:self-start">
                        <Assistant />
                    </div>
                </div>
            </div>
        </div>
    );
}
